import React, { useEffect, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface EmployeeWiseSalesRow {
    id_users: number;
    employee_name: string;
    total_orders: number;
    total_invoiced_amount: number;
    total_collection_amount: number;
    total_shipment_amount: number;
}

const EmployeeWiseSalesReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<EmployeeWiseSalesRow[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [employees, setEmployees] = useState<any[]>([]);
    const [employee, setEmployee] = useState({ label: 'All Employees', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        idUsers: query.get('idUsers') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item: any) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? projectObj.value : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    useEffect(() => {
        getEmployees();
    }, []);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id_projects,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.log(error));
    };

    const getEmployees = () => {
        fetch(process.env.REACT_APP_API_URL + 'getAllSr', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const employeeOptions = [{ label: 'All Employees', value: '' }];
                resp.forEach((item: any) => {
                    const capitalizedStatus = item.status?.charAt(0).toUpperCase() + (item.status?.slice(1) ?? '');
                    employeeOptions.push({
                        label: `${item.first_name} ${item.last_name} (${item.assigned_projects ?? 0} projects, ${capitalizedStatus})`,
                        value: item.id,
                    });
                });
                setEmployees(employeeOptions);
            })
            .catch((error) => console.log(error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setEmployee({ label: 'All Employees', value: '' });
        setFormData((prev) => ({
            ...prev,
            idProjects: selected?.value ?? '',
            idUsers: '',
        }));
    };

    const handleEmployeeChange = (selected: any) => {
        setEmployee(selected);
        setFormData((prev) => ({
            ...prev,
            idUsers: selected?.value ?? '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        fetch(process.env.REACT_APP_API_URL + 'mis-reports/employee-wise-sales-report', postRequestOptions(formData))
            .then((resp) => resp.json())
            .then((resp) => {
                setLoading(false);
                if (resp.success) {
                    setReportData(resp.data ?? []);
                } else {
                    toast.error(resp.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
                }
            })
            .catch((error) => {
                setLoading(false);
                console.log(error);
                toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            });
    };

    return (
        <>
            <Card>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project</Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="employee">
                                    <Form.Label>Employee</Form.Label>
                                    <Select
                                        value={employee}
                                        onChange={handleEmployeeChange}
                                        options={employees}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>

                    {reportData.length > 0 && (
                        <Table bordered hover responsive size="sm" className="mt-4">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th className="text-end">Total Orders</th>
                                    <th className="text-end">Invoiced Amount</th>
                                    <th className="text-end">Collection Amount</th>
                                    <th className="text-end">Shipment Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((row) => (
                                    <tr key={row.id_users}>
                                        <td>{row.employee_name}</td>
                                        <td className="text-end">{numberFormat(row.total_orders)}</td>
                                        <td className="text-end">{numberFormat(row.total_invoiced_amount)}</td>
                                        <td className="text-end">{numberFormat(row.total_collection_amount)}</td>
                                        <td className="text-end">{numberFormat(row.total_shipment_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default EmployeeWiseSalesReport;
